import { toast, useLocalization, createMagicDoorContext, createLazyResource, createMutation } from '@magicdoorinc/ui';
import { createMemo, createSignal } from 'solid-js';
import { CustomViewColumnLabel } from '~/components/custom-views/CustomViewColumnLabel';
import { DynamicListViewItems } from '~/components/custom-views/DynamicListViewItems';
import { customViewsRepository } from '~/repositories/customViewsRepository';
import type { Option } from '~/components/custom-views/DynamicListViewItems';
import type { CreateCustomViewDto, CustomViewType } from '~/swagger/settings/Api';

export const defaultViewName = 'Default';

export const [CustomViewsProvider, useCustomViews] = createMagicDoorContext('CustomViews', () => {
  const { t } = useLocalization();
  const [selectedViewType, setSelectedViewType] = createSignal<CustomViewType | null>(null);
  const [selectedViewKey, setSelectedViewKey] = createSignal<string | null>(null);

  const [customViews, { refetch: refetchCustomViews, mutate: mutateCustomViews }] = createLazyResource(selectedViewType, async (type) => {
    if (!type) return [];
    const views = await customViewsRepository.getCustomViews(type);
    return views.sort((a) => (a.name === defaultViewName ? -1 : 1));
  });

  const defaultView = createMemo(() => {
    return customViews()?.find((view) => view.name === defaultViewName);
  });

  const customView = createMemo(() => {
    const key = selectedViewKey();
    const type = selectedViewType();
    const _customViews = customViews();
    if (!type || !key || !_customViews?.length) return null;

    return _customViews.find((view) => view.key === key);
  });

  const handleCreateCustomView = createMutation(async (payload: CreateCustomViewDto) => {
    const type = selectedViewType();
    if (!type) throw new Error('View type must be selected');

    const result = await customViewsRepository.createCustomView(type, payload);
    toast.success(t('Custom view created successfully'));
    mutateCustomViews((prev) => (prev ? [...prev, result] : [result]));
    setSelectedViewKey(result.key);
    await refetchCustomViews();

    return result;
  });

  const handleUpdateCustomView = createMutation(async (payload: CreateCustomViewDto) => {
    const type = selectedViewType();
    const key = selectedViewKey();
    if (!type || !key) throw new Error('View type and key must be selected');

    const result = await customViewsRepository.updateCustomView(type, key, payload);
    toast.success(t('Custom view updated successfully'));
    mutateCustomViews((prev) => (prev ? prev.map((view) => (view.key === key ? result : view)) : prev));
    refetchCustomViews();
    return result;
  });

  const handleDeleteCustomView = createMutation(async () => {
    const type = selectedViewType();
    const key = selectedViewKey();
    if (!type || !key) throw new Error('View type and key must be selected');

    await customViewsRepository.deleteCustomView(type, key);
    toast.success(t('Custom view deleted successfully'));
    refetchCustomViews();
    setSelectedViewKey(null);
  });

  const selectViewType = (type: CustomViewType) => {
    setSelectedViewType(type);
    setSelectedViewKey(null);
  };

  const selectView = (type: CustomViewType, key: string) => {
    setSelectedViewType(type);
    setSelectedViewKey(key);
  };

  const initialView = createMemo(() => {
    const _customViews = customViews();
    let target = null;

    if (!_customViews?.length) return null;

    target = _customViews[0];

    const favorite = _customViews.find((view) => view.favorite);

    if (favorite) {
      target = favorite;
    }

    return target;
  });

  const [fullColumns, setFullColumns] = createSignal<Option[]>([]);

  const columnsForSelect = createMemo(() => {
    const _columns = fullColumns();
    if (!_columns?.length) return [];

    return _columns
      .filter((col) => !DynamicListViewItems.isSpecialColumn(col))
      .map((col) => ({
        value: DynamicListViewItems.getOptionKey(col),
        label: <CustomViewColumnLabel title={col.title} isMagicTag={DynamicListViewItems.isCustomColumn(col)} />,
      }));
  });

  return {
    selectedViewType,
    selectedViewKey,
    customViews,
    customView,
    handleCreateCustomView,
    handleUpdateCustomView,
    handleDeleteCustomView,
    selectViewType,
    selectView,
    refetchCustomViews,
    defaultView,
    mutateCustomViews,
    initialView,
    setSelectedViewType,
    setSelectedViewKey,
    setFullColumns,
    fullColumns,
    columnsForSelect,
  };
});
